// Entity factory functions — extracted verbatim from partners-portal.jsx.
// Build default-shaped partner/prospect/pool/teaser/audit objects. Pure.

import { addDays, addMonths } from './dates';

export const createPartner = (name, contactPerson, email, phone, agreementType, startDate, endDate, additionalDates = {}) => {
  const id = `partner_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const start    = startDate ? new Date(startDate) : new Date();
  const end      = endDate   ? new Date(endDate)   : addMonths(new Date(), 12);
  const tailMonths = additionalDates.tailMonths || 18;
  const tailEnd  = addMonths(end, tailMonths);

  // Never default to 'Terminated' — a contract whose start date is in the future
  // is a signed, upcoming agreement, not a dead one. Only mark Terminated once
  // the end (and any tail period) has actually passed. Mirrors the guard in
  // checkAutomatedProcesses / updatePartner.
  let status = 'Active';
  const now = new Date();
  if (now > end && now <= tailEnd) {
    status = 'In Tail';
  } else if (now > tailEnd) {
    status = 'Terminated';
  }

  return {
    id,
    name,
    contactPerson,
    email,
    phone,
    agreementType,
    // Sectors & Geography
    sectors: additionalDates.sectors || [],
    geography: additionalDates.geography || { continents: [], countries: [] },
    // Contract dates
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    tailEndDate: tailEnd.toISOString(),
    tailMonths,
    status,
    // Activity tracking
    lastLoginDate: additionalDates.lastLoginDate || null,
    lastActivityDate: additionalDates.lastActivityDate || null,
    // Administrative
    createdAt: new Date().toISOString(),
    lastUpdatedDate: new Date().toISOString(),
    lastModifiedBy: additionalDates.lastModifiedBy || 'System',
    // Commission terms — per-partner overrides of DEFAULT_COMMISSION_TERMS
    commissionTerms: additionalDates.commissionTerms || null,
    // NDA / MNDA
    ndaSigned: additionalDates.ndaSigned || false,
    ndaType: additionalDates.ndaType || null,
    ndaSignedDate: additionalDates.ndaSignedDate || null,
  };
};

export const createProspect = (data, introducingPartnerId, createdByUserId, autoRejectDays = 7) => {
  const id = `prospect_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const approvalRequestDate = new Date().toISOString();
  const autoRejectDeadline = addDays(new Date(), autoRejectDays).toISOString();

  return {
    id,
    createdByUserId,
    introducingPartnerId,
    prospectType: data.prospectType || 'Client',   // Angel | InvestmentBody | Client | Connection
    contractType: data.contractType || 'Direct',    // Direct (EvE ↔ Prospect) | ThroughPartner (EvE ↔ Partner)
    // Shared fields
    name: data.name || data.companyName || '',
    companyName: data.companyName || data.name || '',
    sector: data.sector || '',
    country: data.country || '',
    description: data.description || '',
    notes: data.notes || '',
    // Angel & InvestmentBody fields
    investmentFocus: data.investmentFocus || '',
    typicalInvestmentSize: data.typicalInvestmentSize || '',
    // InvestmentBody specific
    investmentBodyType: data.investmentBodyType || '',  // VC | FamilyOffice | PE | Debt | Other
    preferredStage: data.preferredStage || '',
    // Client specific
    serviceTypes: data.serviceTypes || [],              // ['Retainer', 'Success', 'Equity']
    fundraisingMin: data.fundraisingMin || '',
    fundraisingMax: data.fundraisingMax || '',
    // Connection specific
    potentialDescription: data.potentialDescription || '',
    // Deal Intelligence (Client + shared)
    websiteUrl: data.websiteUrl || '',
    linkedinPerson: data.linkedinPerson || '',
    linkedinCompany: data.linkedinCompany || '',
    investorsDeckUrl: data.investorsDeckUrl || '',
    investorDeckFileUrl:  data.investorDeckFileUrl  || '',
    investorDeckFileName: data.investorDeckFileName || '',
    investorDeckFileSize: data.investorDeckFileSize || 0,
    stage: data.stage || '',
    targetRaise: data.targetRaise || '',
    currentValuation: data.currentValuation || '',
    revenue: data.revenue || '',
    // Status
    approvalStatus: 'Waiting for Approval',
    approvalRequestDate,
    approvalDate: null,
    autoRejectDeadline,
    introDeadline: null,
    introOccurred: false,
    introDate: null,
    validityEndDate: null,
    tailEligible: false,
    teaserIds: [],
    createdAt: new Date().toISOString()
  };
};

export const createPool = (transactionId, participants) => {
  return {
    id: `pool_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    transactionId,
    participants, // [{id, name, type, sharePercent}]
    considerationReceivedDate: null,
    poolCalculatedDate: null,
    notes: '',
    createdAt: new Date().toISOString()
  };
};

export const createTeaser = (data, visibility) => {
  return {
    id: `teaser_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    companyName: data.companyName,
    opportunityName: data.opportunityName || '',
    summary: data.summary || '',
    content: data.content || '',
    prospectId: data.prospectId || null,
    version: '1.0',
    visibility,
    lastUpdated: new Date().toISOString(),
    createdAt: new Date().toISOString()
  };
};

export const logAudit = (entityType, entityId, action, userId, details = '') => {
  return {
    id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    entityType,
    entityId,
    action,
    userId,
    details,
    createdAt: new Date().toISOString()
  };
};
